import React, { useState } from 'react';
import { mockData } from '../mock';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '../components/ui/accordion';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { useToast } from '../hooks/use-toast';
import { Check, Users, Target, Shield, TrendingUp, MessageSquare, BookOpen } from 'lucide-react';

const Home = () => {
  const { toast } = useToast();
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    role: '',
    email: '',
    message: ''
  });

  const handleSubmit = (e) => {
    e.preventDefault();
    // Mock submission
    toast({
      title: "Anfrage gesendet!",
      description: "Wir melden uns in Kürze bei Ihnen.",
    });
    setFormData({ name: '', company: '', role: '', email: '', message: '' });
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const benefitIcons = [MessageSquare, Users, Target, TrendingUp, Shield, BookOpen];

  return (
    <div className="landing-page">
      {/* Navigation */}
      <nav className="main-nav">
        <div className="container nav-container">
          <div className="nav-brand">
            <h2>IT-Supervision</h2>
          </div>
          <div className="nav-links">
            <a href="#warum" className="nav-link">Warum</a>
            <a href="#formate" className="nav-link">Formate</a>
            <a href="#vorgehen" className="nav-link">Vorgehen</a>
            <a href="#faq" className="nav-link">FAQ</a>
            <Button 
              className="btn-primary btn-small" 
              onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}
            >
              Kontakt
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="hero-section">
        <div className="hero-background">
          <div className="hero-image-placeholder">
            <span className="placeholder-text">Bild-Platzhalter: IT-Team Zusammenarbeit, Workshop oder moderne Arbeitsumgebung</span>
          </div>
          <div className="hero-overlay"></div>
        </div>
        <div className="hero-content">
          <h1 className="hero-title">{mockData.hero.title}</h1>
          <p className="hero-subtitle">{mockData.hero.subtitle}</p>
          <div className="hero-actions">
            <Button className="btn-primary" size="lg" onClick={() => document.getElementById('contact').scrollIntoView({ behavior: 'smooth' })}>
              {mockData.hero.cta}
            </Button>
          </div>
          <p className="trust-line">{mockData.hero.trustLine}</p>
        </div>
      </section>

      {/* Challenges Section */}
      <section className="challenges-section" id="warum">
        <div className="container">
          <h2 className="section-title">Warum Supervision in IT-Teams?</h2>
          <div className="challenges-grid">
            {mockData.challenges.map((challenge, index) => (
              <div key={index} className="challenge-item">
                <Check className="challenge-icon" />
                <p>{challenge}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="benefits-section">
        <div className="container">
          <h2 className="section-title">Nutzen für Ihr Team</h2>
          <div className="benefits-grid">
            {mockData.benefits.map((benefit, index) => {
              const Icon = benefitIcons[index];
              return (
                <Card key={index} className="benefit-card">
                  <CardHeader>
                    <Icon className="benefit-icon" />
                    <CardTitle>{benefit.title}</CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription>{benefit.description}</CardDescription>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Image Placeholder Section */}
      <section className="image-section">
        <div className="container">
          <div className="image-grid">
            <div className="image-placeholder large">
              <span className="placeholder-text">Bild-Platzhalter: IT-Team in Supervision/Workshop</span>
            </div>
            <div className="image-placeholder">
              <span className="placeholder-text">Bild-Platzhalter: Teamarbeit</span>
            </div>
            <div className="image-placeholder">
              <span className="placeholder-text">Bild-Platzhalter: Moderne IT-Umgebung</span>
            </div>
          </div>
        </div>
      </section>

      {/* Formats Section */}
      <section className="formats-section" id="formate">
        <div className="container">
          <h2 className="section-title">Formate</h2>
          <div className="formats-grid">
            {mockData.formats.map((format, index) => (
              <Card key={index} className="format-card">
                <CardHeader>
                  <CardTitle>{format.title}</CardTitle>
                  <CardDescription>{format.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <p className="format-details">{format.details}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Topics Section */}
      <section className="topics-section">
        <div className="container">
          <h2 className="section-title">Inhalte & typische Anliegen</h2>
          <div className="topics-grid">
            {mockData.topics.map((topic, index) => (
              <div key={index} className="topic-item">
                <h3 className="topic-title">{topic.title}</h3>
                <p className="topic-description">{topic.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section" id="vorgehen">
        <div className="container">
          <h2 className="section-title">Vorgehen</h2>
          <div className="process-timeline">
            {mockData.process.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
              </div>
            ))}
          </div>
          <div className="process-principles">
            <p><strong>Arbeitsprinzipien:</strong> allparteilich, lösungs- und ressourcenorientiert, strukturiert, praxisnah.</p>
          </div>
        </div>
      </section>

      {/* Target Groups Section */}
      <section className="target-section">
        <div className="container">
          <h2 className="section-title">Für wen geeignet?</h2>
          <div className="target-list">
            {mockData.targetGroups.map((group, index) => (
              <div key={index} className="target-item">
                <Users className="target-icon" />
                <p>{group}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="faq-section" id="faq">
        <div className="container">
          <h2 className="section-title">Häufig gestellte Fragen</h2>
          <Accordion type="single" collapsible className="faq-accordion">
            {mockData.faqs.map((faq, index) => (
              <AccordionItem key={index} value={`item-${index}`}>
                <AccordionTrigger>{faq.question}</AccordionTrigger>
                <AccordionContent>{faq.answer}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Contact Section */}
      <section className="contact-section" id="contact">
        <div className="container">
          <h2 className="section-title">Unverbindliches Erstgespräch anfragen</h2>
          <Card className="contact-card">
            <CardContent>
              <form onSubmit={handleSubmit} className="contact-form">
                <div className="form-group">
                  <Label htmlFor="name">Name *</Label>
                  <Input
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Max Mustermann"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="company">Unternehmen</Label>
                  <Input
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    placeholder="Ihr Unternehmen"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="role">Rolle</Label>
                  <Input
                    id="role"
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    placeholder="z.B. IT-Leitung, Team Lead"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="email">E-Mail *</Label>
                  <Input
                    id="email"
                    name="email"
                    type="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="ihre.email@beispiel.de"
                  />
                </div>
                <div className="form-group">
                  <Label htmlFor="message">Nachricht</Label>
                  <Textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="Beschreiben Sie kurz Ihr Anliegen..."
                    rows={4}
                  />
                </div>
                <Button type="submit" className="btn-primary" size="lg">
                  Anfrage senden
                </Button>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-section">
              <h3>Kontakt</h3>
              <p>[Name/Organisation]</p>
              <p>[E-Mail] • [Telefon]</p>
            </div>
            <div className="footer-section">
              <h3>Qualitätsrahmen</h3>
              <p>Vertraulichkeit und sichere Räume für offene Reflexion</p>
              <p>Klare Ziele und dokumentierte Verabredungen</p>
            </div>
            <div className="footer-section">
              <h3>Hinweis</h3>
              <p>Keine Psychotherapie – arbeitsweltliche Reflexion & Teamentwicklung</p>
            </div>
          </div>
          <div className="footer-bottom">
            <p>&copy; 2025 [Ihr Name/Organisation]. Alle Rechte vorbehalten.</p>
            <div className="footer-links">
              <a href="#">Impressum</a>
              <a href="#">Datenschutz</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Home;
